import { linkClasses } from "@mui/material/Link";
import type { ThemeOptions } from "@mui/material/styles";

import { BOTTOM_NAV_HEIGHT, gradients, MOTION, RADIUS, SHADOW } from "./tokens";
import { textVariantMapping } from "./typography";

/**
 * Component overrides that give the app its Elyxeer look: soft round-cornered
 * surfaces, hairline borders, violet-tinted elevation, and motion that is calm
 * rather than springy.
 */
export const components: ThemeOptions["components"] = {
  MuiTypography: {
    defaultProps: {
      // Give the named variants sensible semantic elements (H* → headings, T* → <p>,
      // L*/OV1 → <span>, HL* → <a>). Merged over MUI's own defaults.
      variantMapping: textVariantMapping,
    },
  },

  MuiCssBaseline: {
    styleOverrides: {
      "html, body, #root": { height: "100%" },
      body: {
        WebkitFontSmoothing: "antialiased",
        // The Science Silver gradient is the brand's signature backdrop. Fixed so it
        // does not slide with the content on a long feed.
        backgroundImage: "var(--elyxeer-page-gradient)",
        backgroundAttachment: "fixed",
        // Never let a horizontal scrollbar appear on a phone-width viewport.
        overflowX: "hidden",
      },
      // Disable the iOS long-press callout and the blue tap highlight so the
      // WebView stops feeling like a web page.
      "*": { WebkitTapHighlightColor: "transparent" },
      "::-webkit-scrollbar": { width: 6, height: 6 },
      "::-webkit-scrollbar-thumb": {
        background: "var(--mui-palette-divider)",
        borderRadius: 3,
      },
      "@media (prefers-reduced-motion: reduce)": {
        "*": {
          animationDuration: "0.01ms !important",
          transitionDuration: "0.01ms !important",
        },
      },
    },
  },

  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { backgroundImage: "none", borderRadius: RADIUS.lg },
      // The content surface is white, so an outlined panel would otherwise be a
      // hairline rectangle on the same colour. The soft violet-tinted lift is what
      // separates it now; the border stays for high-contrast and forced-colours modes.
      outlined: {
        borderColor: "var(--mui-palette-divider)",
        boxShadow: SHADOW.card,
      },
    },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: RADIUS.full,
        paddingInline: 24,
        minHeight: 48, // comfortable touch target on a phone
        transition: `background-color ${MOTION.transition}, color ${MOTION.transition}`,
      },
      outlined: { borderWidth: 1, "&:hover": { borderWidth: 1 } },
    },
    variants: [
      {
        props: { variant: "contained", color: "primary" },
        style: {
          boxShadow: SHADOW.card,
          "&:hover": {
            backgroundColor: "var(--mui-palette-primary-dark)",
            boxShadow: SHADOW.card,
          },
        },
      },
    ],
  },

  MuiIconButton: {
    styleOverrides: { root: { borderRadius: RADIUS.full } },
  },

  // A link that opens an external page is a `<button>` here, not an `<a href>` — the
  // WebView has no chrome to come back from one with. MUI aligns that button `middle`,
  // which lifts it off the baseline of the sentence it sits in and, because it is an
  // inline-block, makes every line containing one taller than its neighbours. Inside
  // running text a link is a word: baseline-aligned, on the paragraph's own leading.
  MuiLink: {
    styleOverrides: {
      root: {
        [`&.${linkClasses.button}`]: {
          verticalAlign: "baseline",
          lineHeight: "inherit",
          // MUI resets the button's user-select; a link in a sentence is still text
          // that a member may want to select along with the sentence around it.
          userSelect: "auto",
        },
      },
    },
  },

  // Fields are filled, not outlined: the label sits inside the field at the top left,
  // with the value below it. The outlined variant notches its label into the border
  // instead, which reads as a caption floating above a detached grey box once the field
  // has a fill — and a select, being always "filled", was permanently in that state.
  MuiTextField: { defaultProps: { variant: "filled" } },

  // The fill is the page colour, not a darker inset: a grey block heavy enough to be
  // read as a filled field on white reads as a dark slab on the page background, which
  // is where most of the app's fields actually sit. What marks the control is the
  // hairline — the same one the outlined Paper uses — so a field is an outline in both
  // schemes rather than a box that changes weight with whatever is behind it.
  MuiFilledInput: {
    defaultProps: { disableUnderline: true },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: RADIUS.md,
        backgroundColor: theme.vars.palette.background.default,
        // 16px keeps iOS Safari from zooming the viewport when an input is focused.
        fontSize: "1rem",
        // Always present so focus does not shift the layout by a pixel.
        border: `1px solid ${theme.vars.palette.divider}`,
        transition: `background-color ${MOTION.transition}, border-color ${MOTION.transition}`,
        // The filled variant draws its underline with pseudo-elements; `disableUnderline`
        // covers the default path, and this covers anything that sets it explicitly.
        "&::before, &::after": { display: "none" },
        "&:hover": {
          backgroundColor: theme.vars.palette.background.default,
          borderColor: theme.vars.palette.text.muted,
        },
        "&.Mui-focused": {
          backgroundColor: theme.vars.palette.background.default,
          borderColor: theme.vars.palette.primary.main,
        },
        "&.Mui-error": { borderColor: theme.vars.palette.error.main },
        // MUI fills a disabled field with `action.disabledBackground`, which would leave
        // the read-only ones as the only grey slabs left. The muted text already says
        // the field is inert.
        "&.Mui-disabled": {
          backgroundColor: theme.vars.palette.background.default,
          borderColor: theme.vars.palette.divider,
        },
      }),
      input: {
        // Room for the shrunk label above the value.
        padding: "25px 16px 8px",
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      filled: ({ theme }) => ({
        color: theme.vars.palette.text.muted,
        // Matches the 16px horizontal padding of the input below it.
        transform: "translate(16px, 18px) scale(1)",
        "&.MuiInputLabel-shrink": {
          transform: "translate(16px, 8px) scale(0.75)",
        },
        "&.Mui-focused": { color: theme.vars.palette.link.main },
      }),
    },
  },

  MuiSelect: {
    styleOverrides: {
      // The select renders its value through the same filled input, so it inherits the
      // padding; only the dropdown arrow needs nudging clear of the rounded corner.
      icon: { right: 12 },
    },
  },

  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: RADIUS.lg,
        border: `1px solid ${theme.vars.palette.divider}`,
        backgroundColor: theme.vars.palette.background.paper,
        boxShadow: SHADOW.card,
      }),
    },
  },

  MuiCardActionArea: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS.lg,
        transition: `transform ${MOTION.transition}`,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: { borderRadius: RADIUS.full, fontWeight: 500 },
    },
  },

  MuiSkeleton: {
    defaultProps: { animation: "wave" },
    styleOverrides: { root: { borderRadius: RADIUS.md } },
  },

  MuiBottomNavigation: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.vars.palette.background.paper,
        borderTop: `1px solid ${theme.vars.palette.divider}`,
        height: BOTTOM_NAV_HEIGHT,
      }),
    },
  },

  MuiBottomNavigationAction: {
    styleOverrides: {
      root: ({ theme }) => ({
        minWidth: 0,
        paddingTop: 8,
        // `primary.main` is a control fill and becomes muddy as a small glyph on the
        // dark navigation surface. `link.main` is the semantic violet for text and
        // icons: deep violet in light mode, high-contrast lavender in dark mode.
        "&.Mui-selected": { color: theme.vars.palette.link.main },
      }),
      label: {
        fontSize: "0.6875rem",
        "&.Mui-selected": { fontSize: "0.6875rem" },
      },
    },
  },

  MuiDivider: {
    styleOverrides: { root: { borderColor: "var(--mui-palette-divider)" } },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: { borderRadius: RADIUS.full, height: 4 },
      bar: { backgroundImage: gradients.pulse },
    },
  },
};
